/*
 * GlossaryInfo — a small "i" affordance shown next to a KPI/column label.
 * On hover it reveals the plain-English definition pulled from the SAME
 * glossary that powers the Glossary page (GET /api/reports/glossary).
 *
 * If no matching definition exists, the component renders nothing — so
 * labels without a glossary entry stay clean.
 *
 * The glossary is fetched once per session and cached module-side.
 */
import React, { useEffect, useState } from "react";
import { Info } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import api from "@/lib/api";
import { lookupMetric } from "@/lib/analyticsGlossary";

let _cache = null;      // { byKey: {}, byLabel: {} }
let _inflight = null;

const _norm = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");

function _buildIndex(glossary) {
    const byKey = {};
    const byLabel = {};
    const hubs = (glossary && glossary.hubs) || {};
    Object.values(hubs).forEach((hub) => {
        (hub.kpis || []).forEach((k) => {
            const def = k.explanation || k.definition || k.description;
            if (!def) return;
            if (k.key) byKey[_norm(k.key)] = def;
            if (k.label) byLabel[_norm(k.label)] = def;
        });
    });
    return { byKey, byLabel };
}

async function _loadGlossary() {
    if (_cache) return _cache;
    if (!_inflight) {
        _inflight = api.get("/reports/glossary")
            .then((r) => { _cache = _buildIndex(r.data); return _cache; })
            .catch(() => { _cache = { byKey: {}, byLabel: {} }; return _cache; });
    }
    return _inflight;
}

export default function GlossaryInfo({ term, termKey, className = "" }) {
    const [def, setDef] = useState(null);

    useEffect(() => {
        let alive = true;
        _loadGlossary().then((idx) => {
            if (!alive) return;
            const hit = (termKey && idx.byKey[_norm(termKey)])
                || (term && idx.byLabel[_norm(term)])
                || lookupMetric(term, termKey)   // dashboard analytics fallback
                || null;
            setDef(hit);
        });
        return () => { alive = false; };
    }, [term, termKey]);

    if (!def) return null;

    return (
        <HoverCard openDelay={120} closeDelay={60}>
            <HoverCardTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                    className={`inline-flex items-center justify-center text-muted-foreground/70 hover:text-primary transition-colors shrink-0 ${className}`}
                    aria-label={`What is ${term}?`}
                    data-testid="glossary-info"
                >
                    <Info className="h-3 w-3" />
                </button>
            </HoverCardTrigger>
            <HoverCardContent className="w-72 text-xs leading-relaxed" align="start" side="top">
                <div className="font-semibold mb-1">{term}</div>
                <div className="text-muted-foreground">{def}</div>
            </HoverCardContent>
        </HoverCard>
    );
}
